import { useMemo } from 'react'
import { Alert, Badge, Paper, Table, Text, Title } from '@mantine/core'
import { channelOf } from '@racedash/shared'
import { useCapturedSessions, type CapturedSession as Row } from '../data/captured'

/**
 * What each channel actually carried for this event, session by session.
 *
 * The evidence for the channel choices above it. RIS publishes the same
 * meeting on several channels, and they are not equally good: at Zandvoort the
 * root carried pit data for 7 of 38 cars on the Saturday morning and for every
 * car from the afternoon on, while /funbe carried it throughout. The car count
 * says which channel carries which championship; the pit-data count says
 * whether a channel can be trusted for stints and stops.
 */
export default function CapturedChannels({ eventId }: { eventId: string }) {
  const { sessions, error } = useCapturedSessions(eventId)
  const rows = useMemo(
    () =>
      sessions
        ? [...sessions].sort(
            (a, b) =>
              (a.firstSeenAt?.toMillis() ?? 0) - (b.firstSeenAt?.toMillis() ?? 0) ||
              (channelOf(a.id) ?? '').localeCompare(channelOf(b.id) ?? ''),
          )
        : null,
    [sessions],
  )

  return (
    <Paper withBorder p="lg">
      <Title order={2} size="h5">
        Captured channels
      </Title>
      <Text c="dimmed" size="sm" mb="md">
        Every session captured for this event, on every channel that carried it. A channel with
        pit data for few of its cars cannot show stints or stops, and is the wrong choice for that
        series.
      </Text>
      {error && <Alert color="red">Could not load the captured sessions: {error}</Alert>}
      {rows && rows.length === 0 && (
        <Text c="dimmed" size="sm">
          Nothing captured for this event yet.
        </Text>
      )}
      {rows && rows.length > 0 && (
        <Table.ScrollContainer minWidth={640}>
          <Table verticalSpacing={4} horizontalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Session, as the channel names it</Table.Th>
                <Table.Th w={80}>Channel</Table.Th>
                <Table.Th w={60} ta="right">
                  Cars
                </Table.Th>
                <Table.Th w={90} ta="right">
                  Pit data
                </Table.Th>
                <Table.Th w={150}>Seen</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => {
                const cars = r.cars?.length ?? 0
                const pit = r.carsWithPitData
                const thin = pit != null && cars > 0 && pit < cars / 2
                return (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Text size="sm">
                        {[r.seriesNameRaw, r.sessionNameRaw].filter(Boolean).join(' — ') || r.id}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="sm" color="gray">
                        {channelOf(r.id) ?? '?'}
                      </Badge>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" ff="monospace">
                        {cars}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" ff="monospace" c={thin ? 'orange' : undefined} fw={thin ? 600 : undefined}>
                        {pit == null ? '—' : `${pit} of ${cars}`}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed" ff="monospace">
                        {span(r)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Paper>
  )
}

function span(r: Row): string {
  const f = r.firstSeenAt?.toDate()
  const l = r.lastSeenAt?.toDate()
  if (!f || !l) return ''
  const day = f.toLocaleDateString(undefined, { weekday: 'short' })
  const hm = (d: Date) => d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  return `${day} ${hm(f)}–${hm(l)}`
}
